import React from "react"
import { ActivityIndicator, Platform, Pressable, TextInput, TextStyle, View, ViewStyle } from "react-native"

import { FontAwesome } from "@expo/vector-icons"
import Toast from "react-native-toast-message"
import { FEEDBACK_GROUPS_TAG_NAMES, FeedbackRating } from "types/feedback"
import { TMessage } from "types/transcript"

import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useAnnotationTags } from "src/queries/useAnnotationTags"
import { useCurrentConversation } from "src/queries/useConversation"
import { ThemedStyle } from "src/theme"
import { Button } from "./Ignite/Button"
import { Text } from "./Ignite/Text"

interface FeedbackFormProps {
  selectedMessage?: TMessage
  onSubmit: (feedback: string, selectedTagIds: string[]) => Promise<void>
  initialRating: FeedbackRating
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  selectedMessage,
  onSubmit,
  initialRating,
}) => {
  const { themed, theme } = useAppTheme()
  const { apiHost } = useAuth()
  const [feedback, setFeedback] = React.useState<string>("")
  const [rating, setRating] = React.useState<FeedbackRating>(initialRating)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [selectedTagIds, setSelectedTagIds] = React.useState<Set<string>>(new Set())
  const { data: _currentConversation } = useCurrentConversation()
  const { data: annotationTags, isLoading: isLoadingTags } = useAnnotationTags()

  const tagsForRating = React.useMemo(() => {
    return annotationTags?.filter((tag) =>
      FEEDBACK_GROUPS_TAG_NAMES.some(
        (group) => group.rating === rating && group.tagNames.includes(tag.name),
      ),
    )
  }, [annotationTags, rating])

  const hasAnnotationTags = tagsForRating && tagsForRating.length > 0

  const _isProdEnvironment = apiHost.split(".")[0] === "prod"

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev)
      if (next.has(tagId)) {
        next.delete(tagId)
      } else {
        next.add(tagId)
      }
      return next
    })
  }

  const handleSubmit = async () => {
    if (isSubmitting || !feedback.trim()) return

    setIsSubmitting(true)

    try {
      await onSubmit(feedback, Array.from(selectedTagIds))

      // Show success message
      Toast.show({
        type: "success",
        text1: "Feedback submitted successfully!",
        text2: "Thank you for your feedback.",
      })

      setIsSubmitting(false)
    } catch (error) {
      console.error("Error submitting feedback:", error)
      Toast.show({
        type: "error",
        text1: "Error submitting feedback",
        text2: "Please try again.",
      })
      setIsSubmitting(false)
    }
  }

  return (
    <View style={themed($container)}>
      {selectedMessage && selectedMessage.text && (
        <View style={$messageBubble}>
          <Text numberOfLines={5} style={themed($messageText)}>
            {selectedMessage.text}
          </Text>
        </View>
      )}

      <View style={themed($ratingContainer)}>
        <Pressable
          style={$iconButton}
          onPress={() =>
            setRating(rating === FeedbackRating.THUMBS_UP ? rating : FeedbackRating.THUMBS_UP)
          }
        >
          <FontAwesome
            name={rating === FeedbackRating.THUMBS_UP ? "thumbs-up" : "thumbs-o-up"}
            size={$ratingIconSize}
            color={theme.colors.buttonText}
          />
        </Pressable>
        <Pressable
          style={$iconButton}
          onPress={() =>
            setRating(rating === FeedbackRating.THUMBS_DOWN ? rating : FeedbackRating.THUMBS_DOWN)
          }
        >
          <FontAwesome
            name={rating === FeedbackRating.THUMBS_DOWN ? "thumbs-down" : "thumbs-o-down"}
            size={$ratingIconSize}
            color={theme.colors.buttonText}
          />
        </Pressable>
      </View>

      {isLoadingTags ? (
        <ActivityIndicator size="small" />
      ) : (
        hasAnnotationTags && (
          <View style={themed($tagsContainer)}>
            {tagsForRating.map((tag) => {
              const isSelected = selectedTagIds.has(tag.id)
              return (
                <Pressable
                  key={tag.id}
                  onPress={() => toggleTag(tag.id)}
                  style={[themed($tagBubble), isSelected && themed($tagBubbleSelected)]}
                >
                  <Text style={[themed($tagText), isSelected && themed($tagTextSelected)]}>
                    {tag.name}
                  </Text>
                </Pressable>
              )
            })}
          </View>
        )
      )}

      <View style={themed($inputWrapperStyle)}>
        <TextInput
          value={feedback}
          onChangeText={setFeedback}
          style={[themed($inputStyle), Platform.OS === "web" && ($inputWebStyle as TextStyle)]}
          multiline
          placeholder="Provide additional feedback"
          placeholderTextColor={theme.colors.textDim}
        />
      </View>
      <Button
        onPress={handleSubmit}
        disabled={isSubmitting || !feedback.trim()}
        preset="sidekick"
        LeftAccessory={
          isSubmitting
            ? () => <ActivityIndicator size="small" color={theme.colors.menuButtonText} />
            : undefined
        }
      >
        {isSubmitting ? "Submitting..." : "Submit"}
      </Button>
    </View>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  gap: spacing.md,
})

const $inputWrapperStyle: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flexDirection: "row",
  alignItems: "flex-start",
  borderWidth: 1,
  borderRadius: 10,
  backgroundColor: colors.inputBackground,
  borderColor: colors.inputBorder,
  overflow: "hidden",
})

const $inputStyle: ThemedStyle<TextStyle> = ({ colors, typography, spacing }) => ({
  flex: 1,
  alignSelf: "stretch",
  fontFamily: typography.primary.normal,
  color: colors.inputText,
  fontSize: 16,
  height: 80,
  // https://github.com/facebook/react-native/issues/21720#issuecomment-532642093
  paddingVertical: 0,
  paddingHorizontal: 0,
  marginVertical: spacing.xs,
  marginHorizontal: spacing.sm,
})

const $ratingContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: spacing.md,
})

const $ratingIconSize = 30

const $iconButton: ViewStyle = {
  padding: 8,
}

const $tagsContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexDirection: "row",
  flexWrap: "wrap",
  gap: spacing.xs,
})

const $tagBubble: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  paddingVertical: spacing.xs,
  paddingHorizontal: spacing.sm,
  borderRadius: 16,
  borderWidth: 1,
  borderColor: colors.border,
  backgroundColor: colors.buttonBackground,
})

const $tagBubbleSelected: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.text,
  borderColor: colors.text,
})

const $tagText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontFamily: typography.primary.normal,
  fontSize: 14,
  color: colors.buttonText,
})

const $tagTextSelected: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.background,
})

const $inputWebStyle = {
  outlineStyle: "none",
  boxShadow: "none",
} as unknown as TextStyle

const $messageBubble: ViewStyle = {
  backgroundColor: "transparent",
  borderWidth: 0,
  padding: 10,
}

const $messageText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontFamily: typography.primary.normal,
  fontSize: 14,
  color: colors.text,
})
